import { Component, OnInit } from "@angular/core";
import { CommonModule, formatDate } from "@angular/common";
import { RouterModule } from "@angular/router";

import { ApiService } from "../../../shared/services/api/api.service";
import { API_ENDPOINTS } from "../../../shared/services/api/api-endpoints";
import { EmptyComponent } from "../../../shared/components/empty/empty.component";
import { SalonComponent } from "../salon/salon.component";
import { LoaderComponent } from "../../../shared/components/loader/loader.component";
import { environment } from "../../../../environments/environment";

export interface FavoriteSalon {
  id: string;
  salon: string;
  salon_name: string;
  salon_city: string;
  salon_description: string;
  is_favorite: boolean;
  created_at: string;
  image?: string;
  rating?: number;
  address?: string;
}

export interface FavoritesApiResponse {
  count: number;
  next: string | null;
  previous: string | null;
  results: FavoriteSalon[];
}

@Component({
  selector: "app-favorites",
  standalone: true,
  imports: [
    CommonModule,
    RouterModule,
    EmptyComponent,
    SalonComponent,
    LoaderComponent,
  ],
  templateUrl: "./favorites.component.html",
  styleUrl: "./favorites.component.scss",
})
export class CustomerFavoritesComponent implements OnInit {
  favouriteSalons: FavoriteSalon[] = [];
  isLoading = false;

  assetUrl = environment.assetBaseUrl;

  constructor(private apiService: ApiService) {}

  ngOnInit(): void {
    this.getFavSalons();
  }

  get reviewedFavorites(): number {
    return this.favouriteSalons.filter((salon) => Number(salon.rating || 0) > 0)
      .length;
  }

  get uniqueCitiesCount(): number {
    const cities = new Set(
      this.favouriteSalons
        .map((salon) => salon.salon_city)
        .filter((city) => !!city),
    );

    return cities.size;
  }

  get averageRating(): string {
    const ratings = this.favouriteSalons
      .map((salon) => Number(salon.rating || 0))
      .filter((rating) => Number.isFinite(rating) && rating > 0);

    if (!ratings.length) return "0.0";

    const average =
      ratings.reduce((sum, rating) => sum + rating, 0) / ratings.length;

    return average.toFixed(1);
  }

  get recentlySavedCount(): number {
    const now = new Date();

    return this.favouriteSalons.filter((salon) => {
      const createdAt = new Date(salon.created_at);

      if (Number.isNaN(createdAt.getTime())) return false;

      const diffMs = now.getTime() - createdAt.getTime();
      const diffDays = diffMs / (1000 * 60 * 60 * 24);

      return diffDays <= 30;
    }).length;
  }

  get favoritesWithPhotos(): number {
    return this.favouriteSalons.filter((salon) => !!salon.image).length;
  }

  get favoritesMissingDetails(): number {
    return this.favouriteSalons.filter((salon) => {
      return !salon.salon_city || !salon.rating || !salon.salon_description;
    }).length;
  }

  getFavSalons(): void {
    this.isLoading = true;

    this.apiService
      .request<FavoritesApiResponse>(
        API_ENDPOINTS.customer.favoritesSalons.list,
        "GET",
      )
      .subscribe({
        next: (response) => {
          this.favouriteSalons = response.results || [];
          this.isLoading = false;
        },
        error: (error) => {
          this.isLoading = false;
          console.error("Error fetching favorite salons:", error);
          this.favouriteSalons = [];
        },
      });
  }

  toggleFavorite(salonUUID: string): void {
    this.isLoading = true;

    this.apiService
      .request<{
        message: string;
        salon_id: string;
        is_favorite: boolean;
      }>(API_ENDPOINTS.customer.favoritesSalons.toggle(salonUUID), "POST", {}, "Favorite updated successfully!")
      .subscribe({
        next: (response) => {
          if (response.is_favorite === false) {
            this.favouriteSalons = this.favouriteSalons.filter(
              (salon) => salon.salon !== response.salon_id,
            );
          } else {
            const salon = this.favouriteSalons.find(
              (item) => item.salon === response.salon_id,
            );

            if (salon) salon.is_favorite = true;
          }

          this.getFavSalons();
        },
        error: (error) => {
          this.isLoading = false;
          console.error("Error toggling favorite:", error);
        },
      });
  }

  trackBySalonId(index: number, salon: FavoriteSalon): string {
    return salon.id;
  }

  formatDate(dateString: string): string {
    if (!dateString) return "recently";

    const date = new Date(dateString);

    if (Number.isNaN(date.getTime())) return "recently";

    return formatDate(date, "MMM d, y", "en-US");
  }

  onImageError(event: any): void {
    event.target.src = this.assetUrl + "img/mock-salon1.png";
  }
}
